// Экран загрузки на странице распознавания: выбор типа, файлы, параметры,
// сводка с запуском и экран обработки. Логика загрузки — в MainDashboard.
import React, { useRef } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  ChevronRight,
  FileText,
  Image as ImageIcon,
  Layers,
  RefreshCw,
  Upload,
  X,
  ArrowRight,
  Clock,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { isCivilDocType, isPagePriced } from "./civilDocs";
import { MAIN_DOC_TYPES, CIVIL_TILES, docTypeMeta, getDocUrl } from "./docTypes";
import { formatRub } from "./PageHeader";

export const ACCEPTED_FILES = ".pdf,.jpg,.jpeg,.png,.doc,.docx";

const plural = (n, one, few, many) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

const formatSize = (bytes) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} КБ`
    : `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} МБ`;

export const filesWord = (n) => `${n} ${plural(n, "файл", "файла", "файлов")}`;

const HINTS = {
  passport: [
    "Заграничный или внутренний паспорт любой страны",
    "Снят несколькими фото — объедините их в один PDF",
    "После распознавания проверьте поля и скачайте перевод по шаблону",
  ],
  drivers_license: [
    "Обе стороны удостоверения — одним PDF",
    "Поддерживаются права любой страны, укажите страну выдачи",
    "Две отдельные фотографии можно объединить в PDF",
  ],
  contract: [
    "Фото, скан или PDF договора, свидетельства, справки",
    "Извлечём полный текст и переведём с сохранением структуры",
    "Результат — DOCX или PDF",
  ],
  civil: [
    "Скан или PDF целиком: с оборотом, приложением, нотариальной надписью и апостилем",
    "Поля переводятся на выбранный язык, шаблон подстраивается под него; ФИО на языки с латиницей — по ICAO, как в загранпаспорте",
    "Для СОР и СОРБ выберите шаблон под образец бланка",
  ],
};

export const hintsFor = (type) =>
  isCivilDocType(type) ? HINTS.civil : HINTS[type] || HINTS.passport;

export const submitLabel = (type, fileCount, cost) => {
  if (fileCount > 1) return `Распознать ${filesWord(fileCount)}`;
  if (type === "contract")
    return cost > 0 ? `Перевести за ${formatRub(cost)}` : "Перевести документ";
  if (type === "drivers_license") return "Распознать права";
  if (isCivilDocType(type)) return "Распознать и перевести";
  return "Распознать паспорт";
};

const StepTitle = ({ n, children }) => (
  <h2 className="rec-step-title">
    <span className="rec-step-num" aria-hidden="true">
      {n}
    </span>
    {children}
  </h2>
);

const CheckBadge = () => (
  <span className="rec-check" aria-hidden="true">
    <Check size={16} strokeWidth={3} />
  </span>
);

const DocTypePicker = ({ value, onChange }) => (
  <>
    <div className="rec-types" role="radiogroup" aria-label="Тип документа">
      {MAIN_DOC_TYPES.map(({ key, label, description, Icon, tone }) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={active}
            className={`rec-type tone-${tone}${active ? " is-active" : ""}`}
            onClick={() => onChange(key)}
          >
            <span className="rec-type-title">{label}</span>
            <span className="rec-type-sub">{description}</span>
            <span className="rec-type-icon">
              <Icon size={26} aria-hidden="true" />
            </span>
            {active && <CheckBadge />}
          </button>
        );
      })}
    </div>

    <p className="rec-subgroup" id="civil-group">
      Документы РФ — перевод по шаблону на любой язык
    </p>
    <div className="rec-civil" role="radiogroup" aria-labelledby="civil-group">
      {CIVIL_TILES.map(({ key, label, compact, description, Icon }) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={active}
            title={`${label}. ${description}`}
            className={`rec-civil-tile tone-butter${active ? " is-active" : ""}`}
            onClick={() => onChange(key)}
          >
            <span className="rec-civil-icon">
              <Icon size={20} aria-hidden="true" />
            </span>
            <span className="rec-civil-label" aria-hidden="true">
              {compact}
            </span>
            <span className="sr-only">{label}</span>
          </button>
        );
      })}
    </div>
  </>
);

const FileRow = ({ file, onRemove }) => {
  const isImage = /\.(jpe?g|png)$/i.test(file.name);
  const Icon = isImage ? ImageIcon : FileText;
  return (
    <li className="rec-file">
      <span className="px-doc-icon" style={{ width: 44, height: 44 }}>
        <Icon size={20} aria-hidden="true" />
      </span>
      <span className="rec-file-text">
        <span className="rec-file-name">{file.name}</span>
        <span className="rec-file-meta">{formatSize(file.size)}</span>
      </span>
      <button
        type="button"
        className="px-icon-btn is-small rec-file-remove"
        aria-label={`Убрать файл ${file.name}`}
        onClick={onRemove}
      >
        <X size={18} aria-hidden="true" />
      </button>
    </li>
  );
};

const SummaryCard = ({
  documentType,
  fileCount,
  user,
  pageCountInfo,
  pageCountLoading,
  canSubmit,
  processing,
  onSubmit,
}) => {
  const meta = docTypeMeta(documentType);
  const isAdmin = Boolean(user?.is_admin);
  const balance = user?.balance ?? 0;
  const freeLeft = user?.company?.free_recognitions ?? 0;
  const free = freeLeft > 0;
  const priced = isPagePriced(documentType) && fileCount === 1 && pageCountInfo;
  const cost = priced && !free ? pageCountInfo.total_cost : 0;
  const short = !isAdmin && priced && cost > balance;

  return (
    <div className="px-cta">
      <div className="px-cta-card rec-summary">
        <div>
          <span className="rec-summary-eyebrow">
            {documentType === "contract" ? "К переводу" : "К распознаванию"}
          </span>
          <h2 className="rec-summary-title">
            <span className="px-cta-pill">{meta.label}</span>
            {fileCount > 0 && <> · {filesWord(fileCount)}</>}
          </h2>
        </div>

        <ul className="rec-hints">
          {hintsFor(documentType).map((h) => (
            <li key={h}>
              <Check size={18} strokeWidth={2.5} aria-hidden="true" />
              {h}
            </li>
          ))}
        </ul>

        {!isAdmin && (
          <dl className="rec-costs">
            {isPagePriced(documentType) && fileCount === 1 && (
              <div>
                <dt>
                  {pageCountLoading
                    ? "Считаем страницы…"
                    : priced
                      ? `${pageCountInfo.page_count} стр. × ${formatRub(pageCountInfo.page_cost)}`
                      : "Стоимость"}
                </dt>
                <dd>{priced ? (free ? "Бесплатно" : formatRub(cost)) : "—"}</dd>
              </div>
            )}
            {free && (
              <div>
                <dt>Бесплатных документов</dt>
                <dd>{freeLeft}</dd>
              </div>
            )}
            <div>
              <dt>Баланс</dt>
              <dd>{formatRub(balance)}</dd>
            </div>
            {priced && !short && !free && (
              <div className="rec-costs-total">
                <dt>Останется</dt>
                <dd>{formatRub(balance - cost)}</dd>
              </div>
            )}
          </dl>
        )}

        {short && (
          <p className="rec-short" role="alert">
            Не хватает {formatRub(cost - balance)}. Пополнить баланс может
            администратор.
          </p>
        )}

        <button
          type="button"
          className="px-btn px-btn-sky rec-submit"
          disabled={!canSubmit}
          onClick={onSubmit}
        >
          {processing ? "Обработка…" : submitLabel(documentType, fileCount, cost)}
          {!processing && <ArrowRight size={20} strokeWidth={2.5} aria-hidden="true" />}
        </button>
        {fileCount === 0 && (
          <p className="rec-summary-note">Сначала добавьте файл</p>
        )}
      </div>
    </div>
  );
};

const RecentResults = ({ items, onRefresh }) => {
  if (!items.length) return null;
  return (
    <section className="rec-recent" aria-labelledby="rec-recent-title">
      <div className="dash-col-head">
        <h2 id="rec-recent-title" className="px-section-title" style={{ fontSize: 18 }}>
          Недавние результаты
        </h2>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            className="px-icon-btn is-small"
            aria-label="Обновить недавние результаты"
            onClick={onRefresh}
          >
            <RefreshCw size={16} aria-hidden="true" />
          </button>
          <Link to="/history" className="px-link" style={{ fontSize: 14 }}>
            Вся история
          </Link>
        </span>
      </div>
      <ul className="rec-recent-list">
        {items.slice(0, 4).map((pf, i) => {
          const meta = docTypeMeta(pf.docType);
          const { Icon } = meta;
          return (
            <li key={`${pf.id}-${i}`}>
              <Link to={getDocUrl(pf.docType, pf.id)} className="rec-recent-item">
                <span className="px-doc-icon" style={{ width: 44, height: 44 }}>
                  <Icon size={20} aria-hidden="true" />
                </span>
                <span className="rec-file-text">
                  <span className="rec-file-name">{pf.name}</span>
                  <span className="rec-file-meta">{meta.fullLabel || meta.label}</span>
                </span>
                <ChevronRight size={18} aria-hidden="true" />
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export const UploadScreen = ({
  documentType,
  onTypeChange,
  fileList,
  isDragging,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileInput,
  onRemoveFile,
  onOpenMerge,
  params,
  summary,
  recent,
  onRefreshRecent,
}) => {
  const inputRef = useRef(null);
  const hasFiles = fileList.length > 0;
  const canMerge = documentType === "passport" || documentType === "drivers_license";

  return (
    <div className="rec-layout">
      <div className="rec-main">
        <section className="rec-step">
          <StepTitle n={1}>Тип документа</StepTitle>
          <DocTypePicker value={documentType} onChange={onTypeChange} />
        </section>

        <section className="rec-step">
          <StepTitle n={2}>Файл</StepTitle>
          <div
            className={`rec-drop${hasFiles ? " is-compact" : ""}${isDragging ? " is-dragging" : ""}`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <span className="rec-drop-icon">
              <Upload size={hasFiles ? 22 : 28} aria-hidden="true" />
            </span>
            <span className="rec-drop-text">
              <span className="rec-drop-title">
                {hasFiles ? "Добавить ещё файлы" : "Перетащите файлы сюда"}
                {" или "}
                <button
                  type="button"
                  className="rec-drop-pick"
                  onClick={() => inputRef.current?.click()}
                >
                  выберите на компьютере
                </button>
              </span>
              <span className="rec-drop-sub">
                PDF, DOCX, JPG или PNG · до 50 МБ · можно несколько файлов
              </span>
            </span>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPTED_FILES}
              onChange={onFileInput}
              hidden
            />
          </div>

          {hasFiles && (
            <ul className="rec-files" aria-label="Выбранные файлы">
              {fileList.map((f, idx) => (
                <FileRow
                  key={`${f.name}-${idx}`}
                  file={f}
                  onRemove={() => onRemoveFile(idx)}
                />
              ))}
            </ul>
          )}

          {canMerge && (
            <button type="button" className="px-chip" style={{ alignSelf: "flex-start" }} onClick={onOpenMerge}>
              <Layers size={18} aria-hidden="true" />
              Объединить несколько фото в PDF
            </button>
          )}
        </section>

        {hasFiles && params && (
          <section className="rec-step">
            <StepTitle n={3}>Параметры</StepTitle>
            {params}
          </section>
        )}
      </div>

      <aside className="rec-side">
        <SummaryCard {...summary} fileCount={fileList.length} documentType={documentType} />
        <RecentResults items={recent} onRefresh={onRefreshRecent} />
      </aside>
    </div>
  );
};

const STATUS = {
  done: { label: "Готово", Icon: Check, cls: "is-done" },
  processing: { label: "Обрабатываем…", Icon: Loader2, cls: "is-processing" },
  pending: { label: "В очереди", Icon: Clock, cls: "is-pending" },
  error: { label: "Ошибка", Icon: X, cls: "is-error" },
};

export const ProcessingScreen = ({ fileStatuses, progress, message }) => {
  const total = fileStatuses.length;
  const multi = total > 1;
  const failed = fileStatuses.filter((f) => f.status === "error").length;
  // при нескольких файлах общий прогресс — по завершённым файлам
  const finished = fileStatuses.filter((f) => f.status === "done" || f.status === "error").length;
  const overall = multi
    ? Math.round(((finished + (progress || 0) / 100) / total) * 100)
    : progress || 0;
  const pct = Math.min(100, Math.max(0, overall));

  return (
    <div className="rec-processing">
      <section className="rec-progress" aria-live="polite">
        <svg className="px-cta-deco rec-progress-deco" viewBox="0 0 220 220" aria-hidden="true">
          <rect x="40" y="30" width="130" height="170" rx="16" fill="none" stroke="#3A3C43" strokeWidth="1.5" transform="rotate(-14 105 115)" />
          <rect x="52" y="34" width="130" height="170" rx="16" fill="none" stroke="#3A3C43" strokeWidth="1.5" transform="rotate(-4 117 119)" />
          <rect x="64" y="40" width="130" height="170" rx="16" fill="none" stroke="#5C5E66" strokeWidth="1.5" transform="rotate(8 129 125)" />
        </svg>
        <div className="rec-progress-head">
          <div>
            <h2 className="rec-progress-title">
              {multi ? "Распознаём" : "Обрабатываем"}{" "}
              <span className="px-cta-pill">{multi ? filesWord(total) : "документ"}</span>
            </h2>
            <p className="rec-progress-msg">{message || "Загрузка файла…"}</p>
          </div>
          <span className="rec-progress-pct">{pct}%</span>
        </div>
        <div
          className="rec-bar"
          role="progressbar"
          aria-label="Общий прогресс"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
        >
          <span style={{ width: `${pct}%` }} />
        </div>
        <p className="rec-progress-note">Не закрывайте вкладку, пока идёт обработка.</p>
      </section>

      {failed > 0 && multi && (
        <div className="rec-warn" role="status">
          <AlertTriangle size={20} aria-hidden="true" />
          {failed === 1
            ? "Один файл не удалось обработать — остальные продолжают распознаваться"
            : `${failed} ${plural(failed, "файл", "файла", "файлов")} не удалось обработать — остальные продолжают распознаваться`}
        </div>
      )}

      {multi && (
        <ul className="rec-status-list" aria-label="Файлы">
          {fileStatuses.map((fs, i) => {
            const st = STATUS[fs.status] || STATUS.pending;
            const { Icon } = st;
            return (
              <li key={`${fs.name}-${i}`} className={`rec-status ${st.cls}`}>
                <span className="rec-status-icon">
                  <Icon size={22} aria-hidden="true" />
                </span>
                <span className="rec-file-text">
                  <span className="rec-file-name">{fs.name}</span>
                  <span className="rec-status-label">
                    {fs.status === "error" && fs.error ? fs.error : st.label}
                  </span>
                </span>
                {fs.status === "done" && fs.id && (
                  <a
                    href={getDocUrl(fs.docType, fs.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-btn px-btn-ink rec-status-open"
                  >
                    Открыть <ArrowRight size={16} strokeWidth={2.5} aria-hidden="true" />
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
